import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  Menu, X, LogIn, LogOut, LineChart, ListChecks, PenLine, BarChart3, LayoutGrid,
  Search, Moon, Sun,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PortfolioSwitcher } from '@/components/PortfolioSwitcher';
import { LiveToggle } from '@/components/LiveToggle';
import { openCommandPalette } from '@/components/CommandPalette';
import { RebalanceBadge } from '@/components/RebalanceBadge';
import { AlertsBadge } from '@/components/AlertsBadge';
import { isDark, toggleMode, subscribeTheme } from '@/lib/theme';
import { useAccess } from '@/lib/access';
import { NAV_TABS, SECTION_ORDER, resolveTabAccess, showInSidebar } from '@/lib/nav';

/**
 * MobileNav — phone/tablet-portrait navigation shell (rendered only below `md`;
 * the desktop top bar in AppShell takes over at ≥768px). Three `md:hidden`
 * pieces: a top app bar (brand + portfolio switcher), a slide-out drawer with
 * the full nav, and a bottom tab bar whose "More" entry opens the drawer.
 */

const BAR_H = 56; // top app-bar height (px), excl. safe-area inset

interface BottomTab {
  to: string;
  label: string;
  icon: LucideIcon;
}

// Access is still enforced by RouteGate — a logged-out tap on "Log" lands on /login.
const BOTTOM_TABS: BottomTab[] = [
  { to: '/performance-summary', label: 'Summary', icon: LineChart },
  { to: '/trade-history', label: 'History', icon: ListChecks },
  { to: '/log-trade', label: 'Log', icon: PenLine },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
];

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, username, authLoading, tabVisibility, refresh: refreshAuth } = useAccess();
  const isAuthed = !!userId;
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState<boolean>(isDark());

  useEffect(() => subscribeTheme((st) => setDark(st.mode === 'dark')), []);

  // Close the drawer whenever the route changes (tapping a link navigates).
  // Derived during render via the prev-value pattern rather than an effect: an
  // effect closes it in a SECOND commit, so the drawer paints once over the new
  // route before disappearing.
  const [prevPath, setPrevPath] = useState(location.pathname);
  if (location.pathname !== prevPath) {
    setPrevPath(location.pathname);
    setOpen(false);
  }

  // While the drawer is open: lock body scroll and close on Escape.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      /* swallow — server clears the cookie regardless */
    }
    refreshAuth();
    setOpen(false);
    navigate('/login');
  }

  const entriesBySection = SECTION_ORDER.map((section) => ({
    section,
    entries: NAV_TABS.filter(
      (t) => t.section === section
        && (t.alwaysShow || showInSidebar(resolveTabAccess(t, tabVisibility), isAuthed)),
    ),
  })).filter((s) => s.entries.length > 0);

  const isActive = (to: string) => location.pathname === to;
  const onPrimaryTab = BOTTOM_TABS.some((t) => t.to === location.pathname);

  return (
    <>
      <header
        className="fixed inset-x-0 top-0 z-40 flex items-center gap-3 border-b border-border bg-card px-3 md:hidden"
        style={{ height: `calc(${BAR_H}px + env(safe-area-inset-top, 0px))`, paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={open}
          className="-ml-1 flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-muted active:bg-muted"
        >
          <Menu className="h-6 w-6" />
        </button>
        <Link to="/performance-summary" className="flex items-center gap-2 no-underline" aria-label="Portfolio Ledger — Performance Summary">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded text-white"
            style={{
              background: 'linear-gradient(135deg, #0F172A, #2563EB)',
              fontFamily: "'Inter Tight', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: '0.78rem',
              letterSpacing: '-0.02em',
            }}
          >
            PL
          </span>
          <span
            className="hidden text-[0.95rem] font-semibold min-[420px]:inline"
            style={{ fontFamily: "'Inter Tight', system-ui, sans-serif", color: 'var(--text)' }}
          >
            Portfolio Ledger
          </span>
        </Link>

        {/* Right cluster: ⌘K search · Live · portfolio switcher (shared context). */}
        <div className="ml-auto flex min-w-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => openCommandPalette()}
            aria-label="Search or jump to"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Search className="h-5 w-5" />
          </button>
          <LiveToggle />
          <div className="min-w-0">
            <PortfolioSwitcher variant="mobile" />
          </div>
        </div>
      </header>

      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        className={cn(
          'fixed inset-0 z-200 bg-black/45 transition-opacity duration-200 md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!open}
        className={cn(
          'fixed inset-y-0 left-0 z-210 flex w-[86vw] max-w-[320px] flex-col border-r border-border bg-card shadow-2xl transition-transform duration-300 ease-out md:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
          <Link to="/performance-summary" className="flex items-center gap-2.5 no-underline" aria-label="Portfolio Ledger — Performance Summary">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded text-white"
              style={{
                background: 'linear-gradient(135deg, #0F172A, #2563EB)',
                fontFamily: "'Inter Tight', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: '-0.02em',
              }}
            >
              PL
            </span>
            <span className="text-sm font-semibold" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif", color: 'var(--text)' }}>
              Portfolio Ledger
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close navigation menu"
            className="-mr-1 flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          {entriesBySection.map(({ section, entries }) => (
            <div key={section} className="mb-4">
              <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section}
              </div>
              <ul className="space-y-0.5">
                {entries.map((entry) => {
                  const Icon = entry.icon;
                  const active = isActive(entry.to);
                  return (
                    <li key={entry.to}>
                      <Link
                        to={entry.to}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-2.5 text-[0.95rem] transition-colors',
                          active
                            ? 'bg-primary/10 font-medium text-primary'
                            : 'text-foreground hover:bg-muted',
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" />
                        <span className="truncate">{entry.label}</span>
                        {entry.to === '/performance-summary/projections' && <RebalanceBadge />}
                        {entry.to === '/performance-summary' && <AlertsBadge />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <button
            type="button"
            onClick={() => toggleMode()}
            className="mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-muted"
          >
            {dark ? <Sun className="h-5 w-5 shrink-0" /> : <Moon className="h-5 w-5 shrink-0" />}
            <span>Dark mode</span>
            <span
              className={cn(
                'ml-auto flex h-[20px] w-[36px] items-center rounded-full px-[2px] transition-colors',
                dark ? 'justify-end bg-primary' : 'justify-start bg-muted-foreground/40',
              )}
              aria-hidden="true"
            >
              <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
            </span>
          </button>
          {authLoading ? (
            <div className="px-2 py-2 text-xs text-muted-foreground">Checking session…</div>
          ) : isAuthed ? (
            <>
              <div className="truncate px-2 pb-1 text-xs text-muted-foreground" title={username || userId || undefined}>
                Signed in as <span className="font-medium text-foreground">{username || userId}</span>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <LogOut className="h-5 w-5 shrink-0" />
                <span>Log out</span>
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-muted"
            >
              <LogIn className="h-5 w-5 shrink-0" />
              <span>Log in</span>
            </Link>
          )}
        </div>
      </aside>

      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border bg-card md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {BOTTOM_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
                active ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <Icon className={cn('h-[1.35rem] w-[1.35rem]', active && 'stroke-[2.4]')} />
              <span>{tab.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="More — open navigation menu"
          className={cn(
            'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
            !onPrimaryTab ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          <LayoutGrid className="h-[1.35rem] w-[1.35rem]" />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}
